import { Component, inject, OnInit, ViewChild } from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HeaderService } from '../services/header/header.service';
import {
  TAB_TITLE,
  COMPONENT_TITLE,
  COMPONENT_DESCRIPTION,
} from '../enums/common';
import { CommonService } from '../services/common/common.service';
import { finalize } from 'rxjs/internal/operators/finalize';
import { environment } from '../../environments/environment';
import { ShareCardComponent } from '../shared/components/share-card/share-card.component';
import { LoaderOverlayComponent } from '../loader-overlay/loader-overlay.component';
import { BotGuardComponent } from '../bot-guard/bot-guard.component';
import { ZIP_URL_FAQ } from '../content/text-faq.content';
import { FaqComponent } from '../faq/faq.component';
import { SeoSchemaService } from '../services/seo/seo-schema.service';
import { CustomLinkComponent } from '../shared/components/custom-link/custom-link.component';
import { PinToggleComponent } from '../shared/components/pin-toggle/pin-toggle.component';
import { OneTimeToggleComponent } from '../shared/components/one-time-toggle/one-time-toggle.component';
import { AdditionalOptionsComponent } from '../shared/components/additional-options/additional-options.component';
import { ShareCardOptionsComponent } from '../shared/components/share-card-options/share-card-options.component';

@Component({
  selector: 'app-zip-url',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ShareCardComponent,
    LoaderOverlayComponent,
    BotGuardComponent,
    FaqComponent,
    CustomLinkComponent,
    PinToggleComponent,
    OneTimeToggleComponent,
    AdditionalOptionsComponent,
    ShareCardOptionsComponent,
  ],
  templateUrl: './zip-url.component.html',
  styleUrl: './zip-url.component.css',
})
export class ZipUrlComponent implements OnInit {
  private readonly headerService = inject(HeaderService);
  private readonly commonService = inject(CommonService);
  private readonly seoSchemaService = inject(SeoSchemaService);
  isRedirect = false;
  readonly urlForm: FormGroup;
  readonly expiryTimes = [
    { text: '10 min', value: 10 },
    { text: '30 min', value: 30 },
    { text: '1 hour', value: 60 },
    { text: '6 hours', value: 360 },
    { text: '1 day', value: 1440 },
    { text: 'No Expiry', value: null },
  ];

  loading = false;
  id: string | null = null;
  faqItems = ZIP_URL_FAQ;
  shortUrl = '';
  showShareCard = false;
  isSlugAvailable: boolean | null = null;
  customSlug: string | null = null;
  checkingSlug = false;
  isExpanded = false;
  isOneTimeView = false;
  hasPin = false;
  @ViewChild(CustomLinkComponent) customLinkComponent!: CustomLinkComponent;
  @ViewChild(PinToggleComponent) pinToggleComponent!: PinToggleComponent;

  toggleAdditionalOptions() {
    this.isExpanded = !this.isExpanded;
  }

  constructor(private fb: FormBuilder) {
    const URL_REGEX =
      /^(?:(?:https?:\/\/)?(?:localhost|(?:\d{1,3}\.){3}\d{1,3}|(?:[A-Za-z0-9-]+\.)+[A-Za-z]{1,}))(?::\d{1,5})?(?:[\/?#][^\s]*)?$/i;

    this.urlForm = this.fb.group({
      url: ['', [Validators.required, Validators.pattern(URL_REGEX)]],
      expiryTime: [this.expiryTimes[5].value],
    });
  }

  ngOnInit(): void {
    this.isRedirect = this.commonService.getIsUrlRedirect();
    this.commonService.setIsUrlRedirect(false);

    this.headerService.setTitleAndDescription({
      pageTitle: COMPONENT_TITLE.ZIP_URL,
      pageDescription: COMPONENT_DESCRIPTION.ZIP_URL,
      tabTitle: TAB_TITLE.ZIP_URL,
    });
    this.seoSchemaService.setFaqSchema(this.faqItems);
  }

  async onSubmit(botGuard: any) {
    const guardResult = botGuard.validate();
    if (!guardResult.valid) {
      console.warn('Blocked by bot guard:', guardResult.reason);
      return;
    }

    if (
      this.customSlug &&
      this.isSlugAvailable === null &&
      !this.checkingSlug
    ) {
      this.customLinkComponent.suppressAvailabilityUi = true; // hide "Available" from blur-triggered check
      this.checkingSlug = true;
      const availability =
        await this.customLinkComponent.checkAvailability(true);
      this.checkingSlug = false;

      // After check completes, block if not available
      if (availability === false) {
        this.customLinkComponent.suppressAvailabilityUi = false;
        return;
      }
      // Re-enable availability UI after a short delay (blur response may still be in flight)
      setTimeout(
        () => (this.customLinkComponent.suppressAvailabilityUi = false),
        800,
      );
    }
    if (this.customSlug && this.isSlugAvailable === false) {
      return;
    }

    if (this.urlForm.valid) {
      const url = this.urlForm.value.url;
      const expiry = this.urlForm.value.expiryTime
        ? parseInt(this.urlForm.value.expiryTime.toString(), 10)
        : null;
      console.log('URL to shorten:', url, expiry);
      this.generateShortUrl(url, expiry);
    }
  }

  generateShortUrl(url: string, expiry: number | null) {
    this.loading = true;
    const pin = this.pinToggleComponent?.pinValue || null;
    this.commonService
      .generateZipShortUrl(
        url,
        expiry,
        this.customSlug,
        this.isOneTimeView,
        pin,
      )
      .pipe(
        finalize(() =>
          setTimeout(() => {
            this.loading = false;
          }, 300),
        ),
      )
      .subscribe({
        next: (response) => {
          const shortUrl = response?.data?.generateUrl;
          if (!shortUrl) {
            throw new Error();
          }
          this.id = shortUrl;
          this.shortUrl = `${environment.angularUrl}/u/${shortUrl}`;
          this.hasPin = !!pin;
          this.showShareCard = true;
        },
        error: (err) => {
          const errMsg = `Error generating link, ${err}`;
          console.error(errMsg);
          alert(errMsg);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  onSlugAvailabilityChange(value: boolean | null): void {
    this.isSlugAvailable = value;
  }

  onSlugChange(slug: string | null): void {
    // Only reset availability if slug actually changed
    // This prevents resetting when blur validation completes and emits the same slug
    const slugChanged = slug !== this.customSlug;
    this.customSlug = slug;

    if (slugChanged) {
      // Reset availability when slug changes (user typed something new)
      this.isSlugAvailable = null;
    }
    // If slug didn't change, keep current availability status (don't reset)
  }

  createNewLink(): void {
    this.showShareCard = false;
    this.shortUrl = '';
    this.id = null;
    this.hasPin = false;
    this.urlForm.reset({ expiryTime: this.expiryTimes[5].value });
    this.isExpanded = false;
    this.isOneTimeView = false;
  }

  deleteShortUrl(): void {
    if (!this.id) return;
    const idToDelete = this.id;
    this.commonService.deleteZipShortUrl(idToDelete).subscribe({
      next: () => {
        this.showShareCard = false;
        this.shortUrl = '';
        this.id = null;
        this.urlForm.reset({ expiryTime: this.expiryTimes[5].value });
        this.isExpanded = false;
        this.isOneTimeView = false;
      },
      error: (err) => {
        console.error('Error deleting URL', err);
        this.showShareCard = false;
        this.shortUrl = '';
        this.id = null;
      },
    });
  }
}
